import React, { useState, useEffect } from 'react';
import {
    Play, Square, RefreshCw, Search, Camera, Terminal, CheckCircle, XCircle,
    Wifi, WifiOff, Activity, MessageSquare, Clock, AlertTriangle
} from 'lucide-react';
import useStore from '../store';

const DoudianWorkspace = () => {
    const { sendCommand, targetId, currentScreenshot, logs } = useStore();
    const [status, setStatus] = useState({ autoReplyEnabled: false, connected: false });
    const [commandLog, setCommandLog] = useState([]);
    const [isLoading, setIsLoading] = useState(null);

    // Filter logs for Doudian-related events
    const doudianLogs = logs.filter(log =>
        log.type?.includes('STATUS') ||
        log.type?.includes('SCREENSHOT') ||
        log.type?.includes('SELECTOR') ||
        log.type?.includes('RESULT')
    ).slice(-20);

    const addLog = (message, type = 'info') => {
        setCommandLog(prev => [...prev.slice(-19), {
            message,
            type,
            time: new Date().toLocaleTimeString()
        }]);
    };

    const executeCommand = async (command, label) => {
        if (!targetId) {
            addLog('No target connected!', 'error');
            return;
        }

        setIsLoading(command);
        addLog(`→ Sending: ${label}`, 'command');

        sendCommand(command, { id: Date.now() }, (response) => {
            setIsLoading(null);
            if (response?.type === 'STATUS_RESULT' && response.payload) {
                setStatus(response.payload);
                addLog(`✓ Status received`, 'success');
            } else {
                addLog(`← Response: ${JSON.stringify(response).substring(0, 100)}`, 'info');
            }
        });

        // Timeout handling
        setTimeout(() => {
            if (isLoading === command) {
                setIsLoading(null);
                addLog(`⚠ Command timeout`, 'warning');
            }
        }, 5000);
    };

    const captureScreenshot = () => {
        if (!targetId) {
            addLog('No target connected!', 'error');
            return;
        }
        setIsLoading('screenshot');
        addLog('→ Capturing screenshot...', 'command');
        sendCommand('GET_SCREENSHOT', { id: Date.now() });
    };

    // Watch for screenshot updates
    useEffect(() => {
        if (currentScreenshot) {
            setIsLoading(null);
            addLog('✓ Screenshot captured!', 'success');
        }
    }, [currentScreenshot]);

    return (
        <div className="flex flex-col h-full bg-gray-900 text-gray-100 overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-800 shrink-0">
                <div className="flex items-center gap-2">
                    <MessageSquare size={18} className="text-orange-400" />
                    <h2 className="text-sm font-bold">Doudian Workspace</h2>
                </div>
                <div className={`flex items-center gap-2 px-2 py-1 rounded-full text-xs ${targetId ? 'bg-green-900/30 text-green-400' : 'bg-red-900/30 text-red-400'}`}>
                    {targetId ? <Wifi size={12} /> : <WifiOff size={12} />}
                    {targetId ? 'Connected' : 'No Target'}
                </div>
            </div>

            {/* Main Content - Two Columns */}
            <div className="flex-1 flex overflow-hidden">
                {/* Left Column: Controls */}
                <div className="w-1/2 border-r border-gray-800 flex flex-col overflow-hidden">
                    {/* Status Card */}
                    <div className="p-3 border-b border-gray-800">
                        <div className="text-xs font-medium text-gray-400 mb-2">Extension Status</div>
                        <div className="grid grid-cols-2 gap-2">
                            <div className={`p-2 rounded-lg flex items-center gap-2 ${status.autoReplyEnabled ? 'bg-green-900/30 border border-green-700' : 'bg-gray-800 border border-gray-700'}`}>
                                {status.autoReplyEnabled ? <CheckCircle size={14} className="text-green-400" /> : <XCircle size={14} className="text-gray-500" />}
                                <span className="text-xs">Auto Reply</span>
                            </div>
                            <div className={`p-2 rounded-lg flex items-center gap-2 ${targetId ? 'bg-blue-900/30 border border-blue-700' : 'bg-gray-800 border border-gray-700'}`}>
                                <Activity size={14} className={targetId ? 'text-blue-400' : 'text-gray-500'} />
                                <span className="text-xs">Connection</span>
                            </div>
                        </div>
                    </div>

                    {/* Command Buttons */}
                    <div className="p-3 flex-1 overflow-y-auto">
                        <div className="text-xs font-medium text-gray-400 mb-2">Control Commands</div>
                        <div className="space-y-2">
                            <button
                                onClick={() => executeCommand('COMMAND', 'Start Auto Reply')}
                                disabled={!targetId || isLoading === 'COMMAND'}
                                className="w-full flex items-center gap-3 p-3 rounded-lg bg-green-600/20 border border-green-600/40 hover:bg-green-600/30 transition-colors disabled:opacity-50"
                            >
                                <Play size={16} className="text-green-400" />
                                <div className="text-left flex-1">
                                    <div className="text-sm font-medium text-green-300">Start Auto Reply</div>
                                    <div className="text-xs text-green-500">Enable automatic customer responses</div>
                                </div>
                            </button>

                            <button
                                onClick={() => sendCommand('COMMAND', { command: 'STOP_AUTO_REPLY', id: Date.now() })}
                                disabled={!targetId}
                                className="w-full flex items-center gap-3 p-3 rounded-lg bg-red-600/20 border border-red-600/40 hover:bg-red-600/30 transition-colors disabled:opacity-50"
                            >
                                <Square size={16} className="text-red-400" />
                                <div className="text-left flex-1">
                                    <div className="text-sm font-medium text-red-300">Stop Auto Reply</div>
                                    <div className="text-xs text-red-500">Disable automatic responses</div>
                                </div>
                            </button>

                            <button
                                onClick={() => sendCommand('COMMAND', { command: 'GET_STATUS', id: Date.now() })}
                                disabled={!targetId}
                                className="w-full flex items-center gap-3 p-3 rounded-lg bg-blue-600/20 border border-blue-600/40 hover:bg-blue-600/30 transition-colors disabled:opacity-50"
                            >
                                <RefreshCw size={16} className={`text-blue-400 ${isLoading === 'GET_STATUS' ? 'animate-spin' : ''}`} />
                                <div className="text-left flex-1">
                                    <div className="text-sm font-medium text-blue-300">Get Status</div>
                                    <div className="text-xs text-blue-500">Query current extension state</div>
                                </div>
                            </button>

                            <button
                                onClick={() => sendCommand('COMMAND', { command: 'SCAN_SELECTORS', id: Date.now() })}
                                disabled={!targetId}
                                className="w-full flex items-center gap-3 p-3 rounded-lg bg-purple-600/20 border border-purple-600/40 hover:bg-purple-600/30 transition-colors disabled:opacity-50"
                            >
                                <Search size={16} className="text-purple-400" />
                                <div className="text-left flex-1">
                                    <div className="text-sm font-medium text-purple-300">Scan Selectors</div>
                                    <div className="text-xs text-purple-500">Debug DOM selectors</div>
                                </div>
                            </button>

                            <button
                                onClick={captureScreenshot}
                                disabled={!targetId || isLoading === 'screenshot'}
                                className="w-full flex items-center gap-3 p-3 rounded-lg bg-yellow-600/20 border border-yellow-600/40 hover:bg-yellow-600/30 transition-colors disabled:opacity-50"
                            >
                                <Camera size={16} className={`text-yellow-400 ${isLoading === 'screenshot' ? 'animate-pulse' : ''}`} />
                                <div className="text-left flex-1">
                                    <div className="text-sm font-medium text-yellow-300">Capture Screenshot</div>
                                    <div className="text-xs text-yellow-500">Take a snapshot of current page</div>
                                </div>
                            </button>
                        </div>
                    </div>
                </div>

                {/* Right Column: Preview & Logs */}
                <div className="w-1/2 flex flex-col overflow-hidden">
                    {/* Screenshot Preview */}
                    <div className="h-1/2 p-3 border-b border-gray-800 flex flex-col">
                        <div className="text-xs font-medium text-gray-400 mb-2 flex items-center gap-2">
                            <Camera size={12} />
                            Screenshot Preview
                        </div>
                        <div className="flex-1 bg-gray-950 rounded-lg overflow-hidden flex items-center justify-center">
                            {currentScreenshot ? (
                                <img
                                    src={currentScreenshot}
                                    alt="Screenshot"
                                    className="max-w-full max-h-full object-contain"
                                />
                            ) : (
                                <div className="text-gray-600 text-xs flex flex-col items-center gap-2">
                                    <Camera size={24} className="opacity-50" />
                                    <span>No screenshot yet</span>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Command Log */}
                    <div className="h-1/2 p-3 flex flex-col">
                        <div className="text-xs font-medium text-gray-400 mb-2 flex items-center gap-2">
                            <Terminal size={12} />
                            Command Log
                        </div>
                        <div className="flex-1 bg-gray-950 rounded-lg p-2 overflow-y-auto font-mono text-xs space-y-1">
                            {commandLog.length === 0 ? (
                                <div className="text-gray-600">No commands executed yet...</div>
                            ) : (
                                commandLog.map((log, idx) => (
                                    <div key={idx} className={`flex gap-2 ${log.type === 'error' ? 'text-red-400' :
                                            log.type === 'success' ? 'text-green-400' :
                                                log.type === 'command' ? 'text-yellow-400' :
                                                    log.type === 'warning' ? 'text-orange-400' :
                                                        'text-gray-400'
                                        }`}>
                                        <span className="text-gray-600">{log.time}</span>
                                        <span>{log.message}</span>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DoudianWorkspace;
